import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { makeChapterAnnotationKey } from '../utils/annotationKeys';

const replaceNote = (notes, note) => {
  const index = notes.findIndex((item) => item.id === note.id);
  if (index === -1) return [...notes, note];
  return notes.map((item, itemIndex) => (itemIndex === index ? note : item));
};

export const addNote = createAsyncThunk('notes/addNote', async ({ userId, verseId, content, book, title, version }, { rejectWithValue }) => {
  try {
    const response = await fetch('/api/note/', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, verse_id: verseId, text: content, book, title, version }),
    });
    if (!response.ok) throw new Error('Failed to add note');
    return await response.json();
  } catch (error) { return rejectWithValue(error.message); }
});

export const fetchUserNotesByChapter = createAsyncThunk('notes/fetchUserNotesByChapter', async ({ book, chapter, version }, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams({ book, chapter });
    const response = await fetch(`/api/note/user/chapter?${params.toString()}`);
    if (!response.ok) throw new Error('Failed to fetch user notes for chapter');
    return { chapterKey: makeChapterAnnotationKey('all', book, chapter), notes: await response.json() };
  } catch (error) { return rejectWithValue(error.message); }
});

export const fetchUserNotes = createAsyncThunk('notes/fetchUserNotes', async (_, { rejectWithValue }) => {
  try {
    const response = await fetch('/api/note/user');
    if (!response.ok) throw new Error('Failed to fetch user notes');
    return await response.json();
  } catch (error) { return rejectWithValue(error.message); }
});

export const fetchNoteById = createAsyncThunk('notes/fetchNoteById', async (id, { rejectWithValue }) => {
  try {
    const response = await fetch(`/api/note/detail/${id}`);
    if (!response.ok) throw new Error('Failed to fetch note');
    return await response.json();
  } catch (error) { return rejectWithValue(error.message); }
});

export const updateNote = createAsyncThunk('notes/updateNote', async ({ id, text, title, book, version }, { rejectWithValue }) => {
  try {
    const response = await fetch(`/api/note/detail/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, title, book, version }),
    });
    if (!response.ok) throw new Error('Failed to update note');
    return await response.json();
  } catch (error) { return rejectWithValue(error.message); }
});

export const deleteNote = createAsyncThunk('notes/deleteNote', async (id, { rejectWithValue }) => {
  try {
    const response = await fetch(`/api/note/detail/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete note');
    return { id, ...(await response.json()) };
  } catch (error) { return rejectWithValue(error.message); }
});

const initialState = {
  userNotes: [], byChapter: {}, selectedNote: null, loading: false,
  loadingByChapter: {}, error: null, errorByChapter: {},
};

const notesSlice = createSlice({
  name: 'notes', initialState, reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addNote.pending, (state) => { state.loading = true; })
      .addCase(addNote.fulfilled, (state, action) => {
        state.loading = false;
        state.userNotes = replaceNote(state.userNotes, action.payload);
        if (action.payload.version && action.payload.book && action.payload.verse_id) {
          const chapter = Math.floor((Number(action.payload.verse_id) % 1000000) / 1000);
          const key = makeChapterAnnotationKey('all', action.payload.book, chapter);
          state.byChapter[key] = replaceNote(state.byChapter[key] || [], action.payload);
        }
      })
      .addCase(addNote.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchUserNotesByChapter.pending, (state, action) => {
        state.loading = true;
        const { book, chapter } = action.meta.arg;
        state.loadingByChapter[makeChapterAnnotationKey('all', book, chapter)] = true;
      })
      .addCase(fetchUserNotesByChapter.fulfilled, (state, action) => {
        state.loading = false;
        state.byChapter[action.payload.chapterKey] = action.payload.notes;
        state.userNotes = [...state.userNotes.filter((note) => !action.payload.notes.some((item) => item.id === note.id)), ...action.payload.notes];
        state.loadingByChapter[action.payload.chapterKey] = false;
      })
      .addCase(fetchUserNotesByChapter.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchUserNotes.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchUserNotes.fulfilled, (state, action) => { state.loading = false; state.userNotes = action.payload; })
      .addCase(fetchUserNotes.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchNoteById.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchNoteById.fulfilled, (state, action) => { state.loading = false; state.selectedNote = action.payload; })
      .addCase(fetchNoteById.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(updateNote.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateNote.fulfilled, (state, action) => {
        state.loading = false;
        state.userNotes = replaceNote(state.userNotes, action.payload);
        Object.keys(state.byChapter).forEach((key) => {
          state.byChapter[key] = replaceNote(state.byChapter[key].filter((note) => note.id !== action.payload.id), action.payload);
        });
      })
      .addCase(updateNote.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(deleteNote.fulfilled, (state, action) => {
        state.userNotes = state.userNotes.filter((note) => note.id !== action.payload.id);
        Object.keys(state.byChapter).forEach((key) => {
          state.byChapter[key] = state.byChapter[key].filter((note) => note.id !== action.payload.id);
        });
      })
      .addCase(deleteNote.rejected, (state, action) => { state.error = action.payload; });
  },
});

export default notesSlice.reducer;
